import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Alert, Button, Tab, Tabs, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PauseRounded from '@mui/icons-material/PauseRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';

import AlphaWolfMark from 'components/Alpha/AlphaWolfMark';
import { podCardTheme } from 'components/Alpha/AlphaPodCardScope';
import LiveAssistAsk from 'components/Alpha/LiveAssist/LiveAssistAsk';
import LiveAssistCard from 'components/Alpha/LiveAssist/LiveAssistCard';
import {
  CONTEXT_KIND,
  LiveAssistCardView,
  LiveAssistConnection,
  LiveAssistState,
  SUPERSEDED_TEXT,
  orderedCards,
  unseenCount,
} from 'components/Alpha/LiveAssist/liveAssistReducer';
import { useLiveAssistStyles } from 'components/Alpha/LiveAssist/liveAssistStyles';
import {
  LiveAssistTiming,
  useLiveAssistStream,
} from 'components/Alpha/LiveAssist/useLiveAssistStream';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { PodFeaturesSelector, ThunkGetPodFeatures } from 'store/podFeatures';
import { UserDataSelector } from 'store/user';
import { isUserAdmin } from 'utils/isUserAdmin';
import { LiveAssistMode } from 'components/Alpha/LiveAssist/liveAssistTarget';
import { getLiveAssistReplayEnabled } from 'components/Alpha/LiveAssist/liveAssistApi';
import { useLiveAssistCallStanding } from 'components/Alpha/LiveAssist/liveAssistCallStore';

export const EXPANDED_STORAGE_KEY = 'mspp.alphaLiveAssist.expanded';

export const STATUS_LABELS = {
  listening: 'Listening',
  paused: 'Paused',
  no_transcript: 'No transcript',
  budget_reached: 'Limit reached',
} as const;

export const CONNECTION_FAILED_TEXT = "Live Assist couldn't stay connected.";
export const RECONNECT_LABEL = 'Reconnect';
export const REPLAY_LABEL = 'Replay a past call';
export const CALL_ENDED_LABEL = 'Call ended';
export const ASK_CLOSED_TEXT = 'Ask works while the call is live.';
export const PAST_CALL_LABEL = 'Past call';

const EMPTY_TEXT = {
  connecting: 'Connecting Alpha to this call…',
  listening: 'Suggestions show up here as the call goes on.',
  paused: 'Listening is paused. Resume to get suggestions again.',
  no_transcript:
    "This call has no live transcript, so Alpha can't suggest anything. You can still ask Alpha.",
  budget_reached:
    'Alpha has made all the suggestions it can for this call. You can still ask Alpha.',
  ended: 'Alpha stopped listening to this call.',
  callEnded: 'The call ended, so Alpha stopped listening.',
  superseded: 'Alpha stopped listening in this window.',
  pastCall: 'Replay this call to see what Alpha suggests on it.',
} as const;

type TabKey = 'feed' | 'ask';

const readExpanded = (): boolean => {
  try {
    return window.localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeExpanded = (value: boolean): void => {
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, String(value));
  } catch {
    return undefined;
  }
};

const CONNECTING: LiveAssistConnection[] = ['idle', 'connecting'];

const idlePastCall = (state: LiveAssistState, mode: LiveAssistMode): boolean =>
  mode === 'completed' && (state.connection === 'idle' || state.connection === 'ended');

const chipLabel = (state: LiveAssistState, mode: LiveAssistMode): string => {
  if (state.superseded) return SUPERSEDED_TEXT;
  if (idlePastCall(state, mode)) return PAST_CALL_LABEL;
  if (state.callEnded) return CALL_ENDED_LABEL;
  if (state.connection === 'reconnecting') return 'Reconnecting…';
  if (state.connection === 'failed') return 'Not connected';
  if (state.connection === 'ended') return 'Not listening';
  if (CONNECTING.includes(state.connection) || state.status === undefined) return 'Connecting…';
  return STATUS_LABELS[state.status];
};

const emptyText = (state: LiveAssistState, mode: LiveAssistMode): string => {
  if (state.superseded) return EMPTY_TEXT.superseded;
  if (idlePastCall(state, mode)) return EMPTY_TEXT.pastCall;
  if (state.callEnded) return EMPTY_TEXT.callEnded;
  if (state.connection === 'ended') return EMPTY_TEXT.ended;
  if (state.status === undefined) return EMPTY_TEXT.connecting;
  return EMPTY_TEXT[state.status];
};

const feedNote = (
  state: LiveAssistState,
  mode: LiveAssistMode,
  cards: LiveAssistCardView[],
): { text: string; after: boolean } | undefined => {
  const text = emptyText(state, mode);
  if (cards.length === 0 || text === EMPTY_TEXT.connecting) return undefined;
  if (text !== EMPTY_TEXT.listening) return { text, after: false };
  return cards.some(card => card.kind !== CONTEXT_KIND) ? undefined : { text, after: true };
};

interface SheetProps {
  callId: number;
  mode: LiveAssistMode;
  attempt: number;
  isAdmin: boolean;
  replayEnabled: boolean;
  timing?: LiveAssistTiming;
}

const LiveAssistSheet: React.FC<SheetProps> = ({
  callId,
  mode,
  attempt,
  isAdmin,
  replayEnabled,
  timing,
}) => {
  const classes = useLiveAssistStyles();
  const theme = useTheme();
  const live = useLiveAssistStream({ callId, mode, attempt, enabled: true, timing });
  const { state } = live;
  const standing = useLiveAssistCallStanding(callId, attempt);
  const visible =
    state.connection !== 'unavailable' && (mode === 'completed' || standing.liveSession);
  const [expanded, setExpanded] = useState(readExpanded);
  const [tab, setTab] = useState<TabKey>('feed');
  const [focusTarget, setFocusTarget] = useState<string | undefined>();
  const [announcing, setAnnouncing] = useState(false);
  const uid = useId().replace(/:/g, '');
  const bodyId = `la-body-${uid}`;
  const feedRef = useRef<HTMLDivElement | null>(null);
  const askTabRef = useRef<HTMLDivElement | null>(null);
  const cards = orderedCards(state);
  const unseen = unseenCount(state);
  const { seen } = live;

  useEffect(() => {
    if (visible && expanded && tab === 'feed' && unseen > 0) seen();
  }, [visible, expanded, tab, unseen, seen]);

  useEffect(() => {
    setAnnouncing(visible);
  }, [visible]);

  useLayoutEffect(() => {
    if (focusTarget === undefined) return;
    setFocusTarget(undefined);
    if (focusTarget === 'askTab') {
      askTabRef.current?.focus();
      return;
    }
    const next = Array.from(
      feedRef.current?.querySelectorAll<HTMLElement>('[data-la-card]') ?? [],
    ).find(element => element.dataset.laCard === focusTarget);
    (next ?? feedRef.current)?.focus();
  }, [focusTarget]);

  if (!visible) return null;

  const toggle = (): void => {
    setExpanded(value => {
      writeExpanded(!value);
      return !value;
    });
  };

  const dismiss = (card: LiveAssistCardView): void => {
    const index = cards.findIndex(item => item.id === card.id);
    const neighbour = cards[index + 1] ?? cards[index - 1];
    live.dismiss(card);
    setFocusTarget(neighbour?.id ?? '');
  };

  const askClosed =
    state.callEnded || state.superseded || (mode === 'completed' && state.connection !== 'open');

  const askFromCard = (question: string): void => {
    if (askClosed) return;
    live.ask(question);
    setTab('ask');
    setFocusTarget('askTab');
  };

  const listening = !state.callEnded && state.connection === 'open' && state.status === 'listening';
  const paused = !state.callEnded && state.connection === 'open' && state.status === 'paused';
  const replayOffered =
    !state.superseded &&
    isAdmin &&
    (replayEnabled || state.replayAvailable) &&
    (!live.replayStarted || idlePastCall(state, mode));
  const showBadge = unseen > 0 && (!expanded || tab !== 'feed');
  const dotClass = listening ? classes.dotListening : paused ? classes.dotPaused : classes.dotIdle;
  const connecting = CONNECTING.includes(state.connection) && cards.length === 0;
  const note = feedNote(state, mode, cards);

  return (
    <section
      className={cx(classes.sheet, expanded && classes.sheetOpen)}
      aria-label="Alpha live assist"
    >
      <div className={classes.visuallyHidden} role="status" aria-live="polite" aria-atomic="true">
        {announcing && <span key={state.announcementSeq}>{state.announcement}</span>}
      </div>
      <div className={classes.header}>
        <button
          type="button"
          className={classes.toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={toggle}
        >
          <AlphaWolfMark size={20} colour={theme.palette.text.primary} />
          <span className={classes.title}>Alpha</span>
          {showBadge && <span className={classes.badge}>{`${unseen} new`}</span>}
          <span className={classes.chevron} aria-hidden="true">
            {expanded ? (
              <ExpandMoreRounded fontSize="small" />
            ) : (
              <ExpandLessRounded fontSize="small" />
            )}
          </span>
        </button>
        <span className={classes.chip}>
          <span className={cx(classes.dot, dotClass)} aria-hidden="true" />
          {chipLabel(state, mode)}
        </span>
        {(listening || paused) && (
          <Button
            size="small"
            className={classes.headerButton}
            disabled={live.commandBusy}
            startIcon={
              paused ? <PlayArrowRounded fontSize="small" /> : <PauseRounded fontSize="small" />
            }
            onClick={paused ? live.resume : live.pause}
          >
            {paused ? 'Resume' : 'Pause'}
          </Button>
        )}
      </div>

      <div
        id={bodyId}
        className={cx(classes.body, !expanded && classes.hiddenPanel)}
        hidden={!expanded}
      >
        {live.notice !== undefined && (
          <Alert
            severity="error"
            className={classes.notice}
            action={
              <Button
                size="small"
                className={classes.noticeButton}
                onClick={() => {
                  const { notice } = live;
                  live.clearNotice();
                  notice?.retry?.();
                }}
              >
                {live.notice.retry === undefined ? 'Dismiss' : 'Try again'}
              </Button>
            }
          >
            {live.notice.message}
          </Alert>
        )}
        {state.connection === 'failed' && (
          <Alert
            severity="error"
            className={classes.notice}
            action={
              <Button size="small" className={classes.noticeButton} onClick={live.retry}>
                {RECONNECT_LABEL}
              </Button>
            }
          >
            {CONNECTION_FAILED_TEXT}
          </Alert>
        )}
        <Tabs
          value={tab}
          onChange={(_event, value: TabKey) => {
            setTab(value);
          }}
          className={classes.tabs}
          aria-label="Alpha live assist views"
        >
          <Tab
            value="feed"
            id={`${bodyId}-feed-tab`}
            aria-controls={`${bodyId}-feed`}
            className={classes.tab}
            label={cards.length > 0 ? `Feed (${cards.length})` : 'Feed'}
          />
          <Tab
            value="ask"
            id={`${bodyId}-ask-tab`}
            aria-controls={`${bodyId}-ask`}
            className={classes.tab}
            label="Ask"
            ref={askTabRef}
          />
        </Tabs>

        <div
          role="tabpanel"
          id={`${bodyId}-feed`}
          aria-labelledby={`${bodyId}-feed-tab`}
          className={cx(classes.tabPanel, tab !== 'feed' && classes.hiddenPanel)}
          hidden={tab !== 'feed'}
        >
          <div className={classes.panel} ref={feedRef} tabIndex={-1}>
            {replayOffered && (
              <div className={classes.replayRow}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.secondaryButton}
                  startIcon={<ReplayRounded fontSize="small" />}
                  disabled={live.commandBusy}
                  onClick={live.replay}
                >
                  {REPLAY_LABEL}
                </Button>
              </div>
            )}
            {cards.length === 0 ? (
              <>
                <p className={classes.empty}>{emptyText(state, mode)}</p>
                {connecting && <div className={classes.skeletonCard} aria-hidden="true" />}
              </>
            ) : (
              <>
                {note !== undefined && !note.after && <p className={classes.empty}>{note.text}</p>}
                <ul className={classes.feedList} aria-label="Alpha suggestions">
                  {cards.map(card => (
                    <li key={card.id}>
                      <LiveAssistCard
                        card={card}
                        sessionId={state.sessionId}
                        onDismiss={dismiss}
                        onTogglePin={live.togglePin}
                        onRate={live.rate}
                        onAsk={askFromCard}
                        askDisabled={askClosed}
                        onRetry={live.refreshContext}
                      />
                    </li>
                  ))}
                </ul>
                {note?.after === true && <p className={classes.empty}>{note.text}</p>}
              </>
            )}
          </div>
        </div>

        <div
          role="tabpanel"
          id={`${bodyId}-ask`}
          aria-labelledby={`${bodyId}-ask-tab`}
          className={cx(classes.tabPanel, tab !== 'ask' && classes.hiddenPanel)}
          hidden={tab !== 'ask'}
        >
          <LiveAssistAsk
            asks={state.asks}
            onAsk={live.ask}
            closedText={
              askClosed ? (state.superseded ? SUPERSEDED_TEXT : ASK_CLOSED_TEXT) : undefined
            }
          />
        </div>
      </div>
    </section>
  );
};

interface Props {
  callId?: number;
  mode?: LiveAssistMode;
  attempt?: number;
  timing?: LiveAssistTiming;
}

const useLiveAssistReplayEnabled = (wanted: boolean): boolean | undefined => {
  const [enabled, setEnabled] = useState<boolean | undefined>();
  useEffect(() => {
    if (!wanted) return undefined;
    let current = true;
    getLiveAssistReplayEnabled().then(value => {
      if (current) setEnabled(value);
    });
    return () => {
      current = false;
    };
  }, [wanted]);
  return wanted ? enabled : undefined;
};

const LiveAssistPanel: React.FC<Props> = ({ callId, mode, attempt = 0, timing }) => {
  const dispatch = useAppDispatch();
  const features = useAppSelector(PodFeaturesSelector);
  const user = useAppSelector(UserDataSelector);
  const isAdmin = isUserAdmin(user?.roleId);
  const standing = useLiveAssistCallStanding(callId, attempt);

  useEffect(() => {
    if (features === null) void dispatch(ThunkGetPodFeatures());
  }, [features, dispatch]);

  const pastCall = features?.alpha === true && mode === 'completed' && isAdmin && !standing.refused;
  const replayEnabled = useLiveAssistReplayEnabled(pastCall);

  if (features?.alpha !== true || typeof callId !== 'number' || mode === undefined) return null;
  if (standing.refused) return null;
  if (mode === 'completed' && (!isAdmin || replayEnabled !== true)) return null;

  return (
    <ThemeProvider theme={podCardTheme}>
      <LiveAssistSheet
        key={`${callId}:${mode === 'completed' ? 'past' : attempt}`}
        callId={callId}
        mode={mode}
        attempt={attempt}
        isAdmin={isAdmin}
        replayEnabled={replayEnabled === true}
        timing={timing}
      />
    </ThemeProvider>
  );
};

export default LiveAssistPanel;
